/**
 * Story 27.4's words — the two dialogs that lead to a proposal, the drafting card, the review.
 *
 * DRAWN is `design/screens/funnels-list.html` (states `new-funnel`, `describe`,
 * `describe-unavailable`, empty) and `design/screens/editor-build.html` (`drafting`, `proposal`),
 * verbatim; `tests/proposal-copy.test.ts` holds each to an element of those files, called with
 * the drawn fiction (Redwood Civil, five drafts, five of six steps). COMPOSED is what no capture
 * draws, held there to NOT being drawn, so a round-trip that draws one moves it here:
 *
 *   • the meaning of the four archetypes the drawing does not show — only the qualifier's is drawn,
 *     so the other four are written from PRD §3.2's own table ("Outcome shows", "Typical use");
 *   • the bands line for a ladder that is not the templates' — the drawn suffix would be untrue;
 *   • the ideal customer's line — DECISION-180 shows the person's words in the review, and the
 *     drawing predates it (the BACKLOG line asks for it drawn);
 *   • the three reasons drafting can be closed that `describe-unavailable` describes and does not
 *     draw — AI switched off, the month's stop, drafting down;
 *   • what a draft that could not be made says, and what a refused partial accept says.
 */
import type { Archetype } from '@leadgible/schema'

const plural = (n: number, noun: string) => `${n} ${noun}${n === 1 ? '' : 's'}`

export const DRAWN = {
  /* funnels-list · new-funnel */
  newFunnelTitle: 'Start a new funnel',
  fromTemplate: 'Start from a template',
  fromTemplateLine: 'Adapt one of the blueprints. The quickest way to a funnel that works.',
  describe: 'Describe your business',
  describeLine: "Tell us what you do and who you want to hear from, and we'll draft a funnel for you to check first.",
  cancel: 'Cancel',

  /* funnels-list · describe-unavailable — the daily limit, the one reason drawn */
  pausedForTheDay: (org: string, limit: number) =>
    `Drafting is paused until tomorrow — ${org} has drafted ${plural(limit, 'funnel')} today, the most for one day.`,

  /* funnels-list · describe */
  describeTitle: 'Describe your business',
  whatItDoes: 'What does your business do?',
  whoFrom: 'Who do you want to hear from?',
  describeHelper: "A sentence or two each is plenty. We'll propose a funnel, and nothing is made until you accept it.",
  back: 'Back',
  draftMyFunnel: 'Draft my funnel',

  /* funnels-list · empty — the second way in */
  emptyBody: "A template gets you to a first scored lead in about ten minutes — or describe your business and we'll draft one.",

  /* funnels-list · error — its own button, which a draft that failed for a moment offers too */
  tryAgain: 'Try again',

  /* editor-build · drafting and proposal — the header while nothing exists */
  untitled: 'Untitled funnel',
  nothingSaved: 'Nothing saved yet',

  /* editor-build · drafting */
  draftingEyebrow: 'Drafting your funnel',
  quoted: (words: string) => `“${words}”`,
  stages: [
    'Reading your description',
    'Choosing the kind of funnel',
    'Writing the questions',
    'Setting how answers are scored',
  ] as const,
  stageDone: ', done',
  stageActive: ', in progress',
  stageWaiting: ', waiting',
  draftingNote: 'About twenty seconds. Nothing is saved until you accept it.',

  /* editor-build · proposal */
  proposedEyebrow: 'Proposed funnel',
  kind: {
    scorecard: 'Scorecard',
    qualifier: 'Qualifier',
    recommender: 'Recommender',
    triage: 'Triage',
    eligibility: 'Eligibility',
  } satisfies Record<Archetype, string>,
  qualifierMeaning: 'A qualifier sorts enquiries into qualified or not, and gives each person a next step.',
  draftAgainAs: 'Not what you meant? Draft it again as',
  steps: 'Steps',
  ticked: (n: number, of: number) => `${n} of ${of} ticked`,
  step: (n: number) => `Step ${n}`,
  scored: 'Scored',
  proposed: 'Proposed',
  include: (n: number, title: string) => `Include step ${n}, ${title}`,
  bandsLikeTemplates: (list: string) => `${list} — the bands every template starts with.`,
  pullsOut: (n: number) => `pulls out ${plural(n, 'fact')}`,
  acceptNote: 'Accepting makes the funnel. Each part you accept can be undone like anything you type.',
  discard: 'Discard',
  acceptSome: (n: number, of: number) => `Accept ${n} of ${of} steps`,
  acceptAll: 'Accept all',
} as const

export const COMPOSED = {
  /** PRD §3.2's table, one archetype a sentence, in the drawn qualifier's shape. */
  meaning: {
    scorecard: 'A scorecard gives each person a score and a band, and shows how they compare.',
    recommender: 'A recommender matches each person to the option or tier that fits them best.',
    triage: 'A triage sends each enquiry to the destination that should handle it.',
    eligibility: 'An eligibility check tells each person yes or no, and why.',
  } satisfies Record<Exclude<Archetype, 'qualifier'>, string>,
  /** The bands line when the ladder is not the templates' — the drawn suffix would be untrue. */
  bandsOwn: (list: string) => `${list}.`,
  /** DECISION-180: the review shows the ideal customer as the person's own words. */
  idealCustomer:
    'Your description becomes its ideal customer, in your own words — the Qualify tab can score how closely each lead matches.',

  /** `describe-unavailable`'s two described reasons, and drafting down. */
  switchedOff: (org: string) =>
    `Drafting is off while AI is switched off for ${org}. An Owner can switch it back on in Usage & cost.`,
  budgetStop: (org: string) =>
    `Drafting is paused until next month — ${org} has reached the AI spend stop set for this one. An Owner can raise it in Usage & cost.`,
  unavailable: "Drafting isn't available just now. The templates are ready whenever you are.",

  /** A draft that could not be made — the eyebrow the drafting card's own, and the way out. */
  failedEyebrow: "Couldn't draft your funnel",
  failedTransient:
    "We couldn't draft a funnel from that just now. Try again in a moment, or start from a template instead.",

  /** The review refusing a set of ticks before anything is made — `simulate` said the save would. */
  partRefused: "Those steps don't make a funnel that can be saved. Tick more of them, or accept all.",
  acceptFailed: "We couldn't make the funnel. Check your connection and try again.",
} as const

/** What the review says an archetype means — the drawn qualifier, or PRD §3.2 for the other four. */
export function meaningOf(archetype: Archetype): string {
  return archetype === 'qualifier' ? DRAWN.qualifierMeaning : COMPOSED.meaning[archetype]
}
